import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { Menu, X, Sun, Moon, Globe } from 'lucide-react';

const LOGO_URL = "/naraze-logo.png";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();

  // Default menus
  const defaultMenus = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'Tentang Kami' },
    { path: '/services', label: 'Layanan' },
    { path: '/portfolio', label: 'Portfolio' },
    { path: '/blog', label: 'Blog' },
    { path: '/contact', label: 'Kontak' },
  ];

  const [navLinks, setNavLinks] = useState(defaultMenus);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMenus = async () => {
      try {
        // Try multiple API URLs as fallback
        const apiUrls = [
          'https://narazesolusiteknologi.id/api/menus.php',
          'https://naraze.business/api/menus.php',
          '/api/menus.php'
        ];

        let data = null;
        for (const url of apiUrls) {
          try {
            const response = await fetch(url, { 
              mode: 'cors',
              cache: 'no-cache'
            });
            if (response.ok) {
              const responseData = await response.json();
              // Handle both { data: [...] } and [...] response format
              data = Array.isArray(responseData) ? responseData : responseData.data;
              if (data && data.length > 0) break;
            }
          } catch (e) {
            console.debug(`API URL failed: ${url}`);
            continue;
          }
        }

        if (data && Array.isArray(data)) {
          const menus = data
            .sort((a, b) => a.order - b.order)
            .map(menu => ({
              path: menu.path,
              label: language === 'en' ? (menu.label_en || menu.label_id) : (menu.label_id || menu.label_en)
            }));
          setNavLinks(menus);
        } else {
          console.warn('No valid menu data received, using defaults');
          setNavLinks(defaultMenus);
        }
      } catch (error) {
        console.error('Failed to fetch menus:', error);
        setNavLinks(defaultMenus);
      } finally {
        setLoading(false);
      }
    };

    if (t) {
      fetchMenus();
    }
  }, [language, t]);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const handleNavClick = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  };

  return (
    <nav 
      data-testid="navbar"
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-background/70 border-b border-border"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2"
            data-testid="navbar-logo"
          >
            <img 
              src={LOGO_URL} 
              alt="Naraze Logo" 
              className="h-10 md:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={handleNavClick}
                data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  isActive(link.path) 
                    ? 'text-primary' 
                    : 'text-muted-foreground'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Language Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleLanguage}
              data-testid="language-toggle"
              className="text-muted-foreground hover:text-primary"
            >
              <Globe className="h-5 w-5" />
              <span className="sr-only">Toggle language</span>
            </Button>
            <span className="text-xs font-medium text-muted-foreground uppercase hidden sm:inline">
              {language}
            </span>

            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className="text-muted-foreground hover:text-primary"
            >
              {theme === 'dark' ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
              <span className="sr-only">Toggle theme</span>
            </Button>

            {/* CTA Button */}
            <Link to="/contact" className="hidden sm:block">
              <Button 
                data-testid="navbar-cta"
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 glow-cyan glow-cyan-hover"
              >
                {t('hero.cta')}
              </Button>
            </Link>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-toggle"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div 
            className="md:hidden py-4 border-t border-border"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => {
                    setIsOpen(false);
                    handleNavClick();
                  }}
                  data-testid={`mobile-nav-link-${link.path.replace('/', '') || 'home'}`}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link to="/contact" onClick={() => setIsOpen(false)}>
                <Button 
                  className="w-full mt-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
                  data-testid="mobile-cta"
                >
                  {t('hero.cta')}
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
